/*
  Le catalogue des stages : la liste, et les quatre ecritures qu'on y fait
  (creer, modifier, activer/desactiver, supprimer).

  Sorti de `adminStore`, ou il partageait un seul `isLoading` avec les
  centres et les sessions : enregistrer un stage faisait croire a la page
  entiere qu'elle rechargeait. Ici, deux etats distincts :

  - `chargement` : la premiere lecture de la liste ;
  - `enregistrement` : une ecriture en cours (le bouton de la fenetre).

  Les ecritures LEVENT leur erreur, l'ecran l'affiche la ou l'on regarde.
  Elles mettent la liste a jour sur place, sans la relire en entier.
*/
import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type { TypeDActivite } from '../lib/seo/stage';
import { trierParTitre, type StageDuCatalogue } from '../lib/stages/catalogue';
import { envoyerImage, supprimerImage } from '../lib/stages/imagesDeStage';

export interface Stage extends StageDuCatalogue {
  created_at?: string;
  /*
    Plaine de vacances, stage a theme ou sejour. La colonne porte un DEFAULT
    en base : une ligne ecrite avant son ajout n'en a pas, `typeDe` retombe
    sur « stage » a la lecture.
  */
  type_activite?: TypeDActivite;
  /*
    Ne se saisit plus : le prix vit sur chaque session, dans la
    programmation. Garde en lecture pour les lignes qui en portent un.
  */
  base_price?: number | null;
}

/** Ce que la fenetre de saisie envoie. */
export interface ChampsDuStage {
  title: string;
  description: string;
  age_min: number;
  age_max: number;
  type_activite: TypeDActivite;
  active: boolean;
  /** Une ligne par élément, déjà nettoyée (`lignesDeMateriel`). */
  materiel_a_prevoir: string[];
  pret_materiel_possible: boolean;
}

/*
  Ou l'enregistrement a echoue, pour que l'ecran sache quoi dire et quoi
  faire ensuite :

  - `photo` : la photo n'est pas partie. En modification, rien n'a ete
    ecrit. En creation, le stage EXISTE (`stageCree`) : l'ecran bascule en
    modification sur lui, pour qu'un nouvel essai ne cree pas de doublon ;
  - `fiche` : la base a refuse la ligne. Rien n'a change.
*/
export class EchecDEnregistrement extends Error {
  constructor(
    readonly etape: 'photo' | 'fiche',
    readonly cause: unknown,
    readonly stageCree?: Stage
  ) {
    super(messageDe(cause));
    this.name = 'EchecDEnregistrement';
  }
}

// Les erreurs Supabase (Storage comme PostgREST) portent toutes un `message`.
function messageDe(error: unknown): string {
  const message = (error as { message?: unknown } | null)?.message;
  return typeof message === 'string' ? message : '';
}

/*
  Les deux compteurs de sessions viennent avec la liste, en une requete :
  PostgREST compte les lignes liees, et le second compte est filtre sur les
  sessions non archivees qui ne sont pas encore terminees.
*/
const COLONNES = '*, toutes:sessions(count), a_venir:sessions(count)';

interface LigneLue extends Omit<Stage, 'nb_sessions' | 'nb_sessions_a_venir'> {
  toutes?: { count: number }[];
  a_venir?: { count: number }[];
}

function lireLigne({ toutes, a_venir, ...stage }: LigneLue): Stage {
  return {
    ...stage,
    nb_sessions: toutes?.[0]?.count ?? 0,
    nb_sessions_a_venir: a_venir?.[0]?.count ?? 0,
  };
}

const aujourdhui = () => new Date().toISOString().slice(0, 10);

interface StageState {
  stages: Stage[];
  chargement: boolean;
  enregistrement: boolean;
  erreurDeChargement: string | null;

  fetchStages: () => Promise<void>;
  /** Cree (sans `id`) ou modifie (avec). Rend la fiche enregistree. */
  saveStage: (params: { id?: string; champs: ChampsDuStage; image?: File | null }) => Promise<Stage>;
  setActive: (id: string, active: boolean) => Promise<Stage>;
  deleteStage: (id: string) => Promise<void>;
}

export const useStageStore = create<StageState>((set, get) => {
  /*
    Remplace (ou ajoute) une fiche dans la liste. Les compteurs de sessions
    ne changent pas quand on edite la fiche : ceux deja connus sont gardes.
  */
  const ranger = (ligne: Omit<Stage, 'nb_sessions' | 'nb_sessions_a_venir'>): Stage => {
    const connu = get().stages.find((s) => s.id === ligne.id);
    const stage: Stage = {
      ...ligne,
      nb_sessions: connu?.nb_sessions ?? 0,
      nb_sessions_a_venir: connu?.nb_sessions_a_venir ?? 0,
    };
    set({ stages: trierParTitre([...get().stages.filter((s) => s.id !== stage.id), stage]) });
    return stage;
  };

  const ecrire = async (id: string, valeurs: Partial<Stage>) => {
    const { data, error } = await supabase.from('stages').update(valeurs).eq('id', id).select().single();
    if (error) throw error;
    return data as unknown as Stage;
  };

  const creer = async (champs: ChampsDuStage) => {
    const { data, error } = await supabase.from('stages').insert([champs]).select().single();
    if (error) throw error;
    return data as unknown as Stage;
  };

  return {
    stages: [],
    chargement: false,
    enregistrement: false,
    erreurDeChargement: null,

    fetchStages: async () => {
      set({ chargement: true, erreurDeChargement: null });
      try {
        const { data, error } = await supabase
          .from('stages')
          .select(COLONNES)
          .eq('a_venir.archived', false)
          .gte('a_venir.end_date', aujourdhui())
          .order('title');
        if (error) throw error;
        set({ stages: trierParTitre(((data ?? []) as unknown as LigneLue[]).map(lireLigne)) });
      } catch (error) {
        set({ erreurDeChargement: messageDe(error) || 'La liste des stages n’a pas pu être chargée.' });
      } finally {
        set({ chargement: false });
      }
    },

    /*
      L'ordre compte, et il n'est pas le meme dans les deux cas.

      En modification, la photo part AVANT la fiche : si elle echoue, rien
      n'est ecrit. Si c'est la fiche qui echoue ensuite, la photo envoyee ne
      sert a rien et repart.

      En creation, il faut l'identifiant pour nommer la photo : la fiche
      d'abord, la photo ensuite.
    */
    saveStage: async ({ id, champs, image }) => {
      set({ enregistrement: true });
      try {
        if (id) {
          let imageUrl: string | undefined;
          if (image) {
            try {
              imageUrl = await envoyerImage(id, image);
            } catch (error) {
              throw new EchecDEnregistrement('photo', error);
            }
          }
          try {
            return ranger(await ecrire(id, imageUrl ? { ...champs, image_url: imageUrl } : champs));
          } catch (error) {
            if (imageUrl) supprimerImage(imageUrl);
            throw new EchecDEnregistrement('fiche', error);
          }
        }

        let cree: Stage;
        try {
          cree = ranger(await creer(champs));
        } catch (error) {
          throw new EchecDEnregistrement('fiche', error);
        }
        if (!image) return cree;

        let imageUrl: string | undefined;
        try {
          imageUrl = await envoyerImage(cree.id, image);
          return ranger(await ecrire(cree.id, { image_url: imageUrl }));
        } catch (error) {
          if (imageUrl) supprimerImage(imageUrl);
          throw new EchecDEnregistrement('photo', error, cree);
        }
      } finally {
        set({ enregistrement: false });
      }
    },

    setActive: async (id, active) => {
      set({ enregistrement: true });
      try {
        return ranger(await ecrire(id, { active }));
      } finally {
        set({ enregistrement: false });
      }
    },

    /*
      La base refuse de supprimer un stage qui porte des sessions (cle
      etrangere) : l'ecran ne le propose d'ailleurs pas. Une fois la ligne
      partie, plus rien ne renvoie a sa photo.
    */
    deleteStage: async (id) => {
      set({ enregistrement: true });
      try {
        const imageUrl = get().stages.find((s) => s.id === id)?.image_url;
        const { data, error } = await supabase.from('stages').delete().eq('id', id).select('id');
        if (error) throw error;
        // Une ligne que RLS cache « se supprime » sans erreur et sans effet.
        if (!data?.length) throw new Error('le stage n’a pas été supprimé');
        set({ stages: get().stages.filter((s) => s.id !== id) });
        if (imageUrl) supprimerImage(imageUrl);
      } finally {
        set({ enregistrement: false });
      }
    },
  };
});
